import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { refreshToken } from '../../../../Redux/Auth/authActions';
import { partnerPortalApi } from '../Services/partnerPortalApi';
import {
  EMPTY_PSM_QUEUE_ALERTS,
  normalizePSMQueueResponse,
} from '../Utils/partnerPsmQueueUtils';
import { resolveAccessToken, usePartnerAccessToken } from './usePartnerAccessToken';
import {
  PARTNER_PAE_QUEUE_CACHE_KEY,
  readPartnerProjectListCache,
  writePartnerProjectListCache,
} from '../Utils/partnerProjectListCache';

const EMPTY_ALERTS = EMPTY_PSM_QUEUE_ALERTS;

const isUnauthorizedError = (error) => {
  const message = (error?.message || '').toLowerCase();
  return message.includes('unauthorized') || message.includes('authentication');
};

const resolveProjectId = (project) => (
  project?.id
  ?? project?.projectId
  ?? project?.project_id
  ?? project?._id
  ?? null
);

export const usePSMProjectQueue = (enabled = true) => {
  const dispatch = useDispatch();
  const accessToken = usePartnerAccessToken();
  const [projects, setProjects] = useState([]);
  const [alerts, setAlerts] = useState(EMPTY_ALERTS);
  const [partnerAdoptionEngineers, setPartnerAdoptionEngineers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const withAuthRetry = useCallback(async (apiCall) => {
    const token = resolveAccessToken(accessToken);
    try {
      return await apiCall(token);
    } catch (err) {
      if (!isUnauthorizedError(err) || !Cookies.get('refreshToken')) {
        throw err;
      }

      await dispatch(refreshToken());
      const refreshedToken = Cookies.get('accessToken');
      if (!refreshedToken) {
        throw new Error('Your session has expired. Please sign in again.');
      }

      return apiCall(refreshedToken);
    }
  }, [accessToken, dispatch]);

  const refreshQueue = useCallback(async () => {
    if (!enabled) {
      setProjects([]);
      setAlerts(EMPTY_ALERTS);
      setPartnerAdoptionEngineers([]);
      setLoading(false);
      return;
    }

    const token = resolveAccessToken(accessToken);
    if (!token) {
      setProjects([]);
      setAlerts(EMPTY_ALERTS);
      setPartnerAdoptionEngineers([]);
      setLoading(false);
      return;
    }

    const cached = readPartnerProjectListCache(PARTNER_PAE_QUEUE_CACHE_KEY);
    if (cached) {
      const { projects: cachedProjects, alerts: cachedAlerts } = normalizePSMQueueResponse(cached);
      setProjects(cachedProjects);
      setAlerts(cachedAlerts);
      setPartnerAdoptionEngineers(cached.partnerAdoptionEngineers || []);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const data = await withAuthRetry((activeToken) => (
        partnerPortalApi.fetchPAEProjectQueue(activeToken)
      ));
      writePartnerProjectListCache(PARTNER_PAE_QUEUE_CACHE_KEY, data);
      const { projects: queueProjects, alerts: queueAlerts } = normalizePSMQueueResponse(data);
      setProjects(queueProjects);
      setAlerts(queueAlerts);
      setPartnerAdoptionEngineers(data?.partnerAdoptionEngineers || []);
    } catch (err) {
      setError(err.message);
      setProjects([]);
      setAlerts(EMPTY_ALERTS);
      setPartnerAdoptionEngineers([]);
    } finally {
      setLoading(false);
    }
  }, [accessToken, enabled, withAuthRetry]);

  useEffect(() => {
    refreshQueue();
  }, [refreshQueue]);

  const updateQueueProject = useCallback((projectId, updates) => {
    if (!projectId || !updates) {
      return;
    }

    setProjects((currentProjects) => currentProjects.map((project) => {
      if (resolveProjectId(project) !== projectId) {
        return project;
      }

      return typeof updates === 'function'
        ? updates(project)
        : { ...project, ...updates };
    }));
  }, []);

  return {
    projects,
    alerts,
    partnerAdoptionEngineers,
    loading,
    error,
    refreshQueue,
    updateQueueProject,
  };
};
